import React, { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest, acquireTokenSilent } from "../authConfig";
import axios from "axios";
import { FaTrash, FaDownload } from "react-icons/fa";

const SharePointFilesTab = ({
  projectId,
  projectName,
  sharePointFolderName,
  setProject,
  createdAt,
}) => {
  const { instance: msalInstance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folderCreated, setFolderCreated] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [sharePointConfig, setSharePointConfig] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const documentLibrary = "Shared Documents";
  const parentFolder = "Projects";

  const fetchSharePointConfig = async (accessToken) => {
    try {
      const tenantId = localStorage.getItem("tenantId");
      if (!tenantId) {
        throw new Error("No tenant ID found. Please re-authenticate.");
      }

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Backend JWT not found. Please re-authenticate.");
      }

      // Fetch SharePoint hostname from /sites/root
      const rootSiteResponse = await axios.get(
        "https://graph.microsoft.com/v1.0/sites/root",
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      const sharePointHostname = rootSiteResponse.data.webUrl.split("/")[2]; // e.g., torontotapers.sharepoint.com

      if (!sharePointHostname) {
        throw new Error(
          "Could not retrieve SharePoint hostname from Microsoft Graph.",
        );
      }

      // Fetch company profile for site name
      const companyResponse = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenantId,
          },
        },
      );
      const siteName = companyResponse.data.sharePointSite || ""; // e.g., "TorontoTapers"
      const sitePath = siteName ? `/sites/${siteName}` : "";

      if (!sitePath) {
        throw new Error(
          "SharePoint site path not configured in company profile.",
        );
      }

      return { sharePointHostname, sitePath, documentLibrary, parentFolder };
    } catch (err) {
      console.error("Error fetching SharePoint config:", err);
      throw err;
    }
  };

  const saveFolderName = async (projectId, folderName) => {
    const token = localStorage.getItem("token");
    const tenantId = localStorage.getItem("tenantId");
    if (!token || !tenantId) {
      throw new Error(
        "Backend JWT or tenant ID not found. Please re-authenticate.",
      );
    }
    try {
      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${projectId}`,
        { sharePointFolderName: folderName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenantId,
          },
        },
      );
      console.log(
        `Saved SharePoint folder name: ${folderName} for project ${projectId}`,
      );
      return response.data;
    } catch (err) {
      console.error("Error saving folder name:", err);
      throw err;
    }
  };

  const getSiteId = async (accessToken, config) => {
    try {
      // Correct URL format: hostname:/sitePath
      const siteUrl = `https://graph.microsoft.com/v1.0/sites/${config.sharePointHostname}:${config.sitePath}`;
      const response = await axios.get(siteUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.id;
    } catch (err) {
      console.error("Error fetching site ID:", err);
      throw err;
    }
  };

  const getDriveId = async (accessToken, siteId) => {
    try {
      const driveUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`;
      const response = await axios.get(driveUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const drives = response.data.value;
      const documentDrive = drives.find((drive) => drive.name === "Documents");
      if (!documentDrive) throw new Error("Document library not found");
      return documentDrive.id;
    } catch (err) {
      console.error("Error fetching drive ID:", err);
      throw err;
    }
  };

  const ensureParentFolder = async (accessToken, siteId, driveId, year) => {
    const projectsFolderUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${parentFolder}`;
    let projectsFolderId;
    try {
      const response = await axios.get(projectsFolderUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      projectsFolderId = response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: parentFolder,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        projectsFolderId = createResponse.data.id;
      } else {
        throw err;
      }
    }

    const yearFolderUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${projectsFolderId}/children/${year}`;
    try {
      const response = await axios.get(yearFolderUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${projectsFolderId}/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: year,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        return createResponse.data.id;
      } else {
        throw err;
      }
    }
  };

  const ensureProjectFolder = async (
    accessToken,
    siteId,
    driveId,
    parentFolderId,
    folderName,
    createdAt,
  ) => {
    const date = new Date(createdAt || new Date());
    const year = date.getFullYear().toString();
    const formattedDate = date.toISOString().split("T")[0];
    const formattedFolderName = `${formattedDate} - ${
      folderName || projectName
    }`;

    const checkUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentFolderId}/children/${encodeURIComponent(
      formattedFolderName,
    )}`;
    try {
      const response = await axios.get(checkUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.id;
    } catch (err) {
      if (err.response?.status === 404) {
        const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentFolderId}/children`;
        const createResponse = await axios.post(
          createUrl,
          {
            name: formattedFolderName,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        return createResponse.data.id;
      } else {
        throw err;
      }
    }
  };

  const fetchFolderContents = async (
    accessToken,
    siteId,
    driveId,
    folderId,
  ) => {
    try {
      const folderUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}/children`;
      const response = await axios.get(folderUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setFiles(response.data.value);
    } catch (err) {
      console.error("Error fetching folder contents:", err);
      throw err;
    }
  };

  const initializeSharePointFolder = async () => {
    if (!isAuthenticated || accounts.length === 0) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const config = await fetchSharePointConfig(accessToken);
      setSharePointConfig(config);

      const siteId = await getSiteId(accessToken, config);
      const driveId = await getDriveId(accessToken, siteId);

      const year = createdAt
        ? new Date(createdAt).getFullYear().toString()
        : new Date().getFullYear().toString();

      const yearFolderId = await ensureParentFolder(
        accessToken,
        siteId,
        driveId,
        year,
      );

      const folderName = sharePointFolderName || projectName;
      const projectFolderId = await ensureProjectFolder(
        accessToken,
        siteId,
        driveId,
        yearFolderId,
        folderName,
        createdAt || new Date(),
      );

      const formattedFolderName = createdAt
        ? `${new Date(createdAt).toISOString().split("T")[0]} - ${folderName}`
        : `${new Date().toISOString().split("T")[0]} - ${folderName}`;

      if (!sharePointFolderName) {
        await saveFolderName(projectId, formattedFolderName);
        setProject((prev) => ({
          ...prev,
          sharePointFolderName: formattedFolderName,
        }));
      }

      setCurrentFolderId(projectFolderId);
      setFolderPath([
        { name: year, id: yearFolderId },
        { name: formattedFolderName, id: projectFolderId },
      ]);
      await fetchFolderContents(accessToken, siteId, driveId, projectFolderId);

      setFolderCreated(true);
      setLoading(false);
    } catch (err) {
      console.error("Error in initializeSharePointFolder:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to load SharePoint files: ${err.message}`,
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (isAuthenticated && accounts.length > 0) {
      initializeSharePointFolder();
    } else {
      setLoading(true);
    }

    return () => {
      isMounted = false;
    };
  }, [
    isAuthenticated,
    accounts,
    projectId,
    projectName,
    sharePointFolderName,
    setProject,
    createdAt,
  ]);

  const createSubfolder = async () => {
    if (!newFolderName) {
      setError("Please enter a folder name.");
      return;
    }

    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);
      const createUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}/children`;

      await axios.post(
        createUrl,
        {
          name: newFolderName,
          folder: {},
          "@microsoft.graph.conflictBehavior": "rename",
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      await fetchFolderContents(accessToken, siteId, driveId, currentFolderId);
      setNewFolderName("");
      setShowModal(false);
    } catch (err) {
      console.error("Error creating subfolder:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to create subfolder: ${err.message}`,
      );
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);

      const deleteUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`;
      await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      await fetchFolderContents(accessToken, siteId, driveId, currentFolderId);
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Error deleting item:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to delete item: ${err.message}`,
      );
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const uploadFile = async (file) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);
      const uploadUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}:/${file.name}:/content`;

      await axios.put(uploadUrl, file, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/octet-stream",
        },
      });
      await fetchFolderContents(accessToken, siteId, driveId, currentFolderId);
    } catch (err) {
      console.error("Error uploading file:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to upload file: ${err.message}`,
      );
    }
  };

  const handleFolderClick = async (folder) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);

      setCurrentFolderId(folder.id);
      setFolderPath([...folderPath, { name: folder.name, id: folder.id }]);
      await fetchFolderContents(accessToken, siteId, driveId, folder.id);
    } catch (err) {
      console.error("Error navigating to folder:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to navigate to folder: ${err.message}`,
      );
    }
  };

  const handleBreadcrumbClick = async (folder, index) => {
    try {
      const account = accounts[0];
      const accessToken = await acquireTokenSilent(
        account,
        loginRequest.scopes,
      );
      const siteId = await getSiteId(accessToken, sharePointConfig);
      const driveId = await getDriveId(accessToken, siteId);

      setCurrentFolderId(folder.id);
      setFolderPath(folderPath.slice(0, index + 1));
      await fetchFolderContents(accessToken, siteId, driveId, folder.id);
    } catch (err) {
      console.error("Error navigating back:", err);
      setError(
        err.message.includes("Invalid token") ||
          err.message.includes("Authentication required")
          ? "Authentication error. Please sign out and sign in again."
          : `Failed to navigate back: ${err.message}`,
      );
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    for (const file of droppedFiles) {
      await uploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const getSharePointLink = () => {
    if (!sharePointConfig || !folderCreated) return "";
    const { sharePointHostname, sitePath } = sharePointConfig;
    const folderNames = folderPath.map((folder) => folder.name).join("/");
    return `https://${sharePointHostname}${sitePath}/${documentLibrary}/${parentFolder}/${folderNames}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-4 text-gray-200">
        Please wait, authenticating with Microsoft...
      </div>
    );
  }

  if (loading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  if (error)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-600 border-l-4 border-red-400 rounded-lg p-4">
        {error}
        <button
          onClick={async () => {
            setError(null);
            setLoading(true);
            try {
              await msalInstance.logoutRedirect();
              window.location.reload();
            } catch (logoutErr) {
              console.error("Logout error:", logoutErr);
              setError(
                "Failed to initiate re-authentication. Please try again.",
              );
              setLoading(false);
            }
          }}
          className="ml-4 bg-blue-500 text-white px-4 py-2 rounded mt-2"
        >
          Sign Out and Retry
        </button>
      </div>
    );

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        <h2 className="text-xl font-semibold text-white tracking-tight mb-4">
          SharePoint Files for {projectName}
        </h2>

        {sharePointConfig && (
          <div className="mb-4">
            <a
              href={getSharePointLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-400 hover:underline text-sm"
            >
              Open in SharePoint
            </a>
          </div>
        )}

        <div className="mb-4">
          {folderPath.map((folder, index) => (
            <span key={folder.id}>
              <button
                onClick={() => handleBreadcrumbClick(folder, index)}
                className="text-orange-400 hover:underline"
              >
                {folder.name}
              </button>
              {index < folderPath.length - 1 && (
                <span className="text-white"> &gt; </span>
              )}
            </span>
          ))}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-orange-400 text-gray-200 px-4 py-2 rounded-md hover:bg-orange-500 transition-colors duration-200 shadow-sm mb-4"
        >
          Create Folder
        </button>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-600 p-6 mb-4 text-center rounded-md bg-gray-600"
        >
          <p className="text-gray-400">Drag and drop files here to upload</p>
        </div>

        {files.length === 0 ? (
          <p className="text-gray-400">No files or folders found.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Last Modified
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {files.map((file) => (
                <tr
                  key={file.id}
                  className="hover:bg-gray-500 transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    {file.folder ? (
                      <button
                        onClick={() => handleFolderClick(file)}
                        className="text-orange-400 hover:underline"
                      >
                        {file.name}
                      </button>
                    ) : (
                      file.name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    {file.folder ? "Folder" : "File"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    {new Date(file.lastModifiedDateTime).toLocaleString(
                      "en-US",
                      {
                        dateStyle: "medium",
                        timeStyle: "short",
                      },
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right flex items-center gap-4">
                    {!file.folder && (
                      <button
                        onClick={() => window.open(file.webUrl, "_blank")}
                        className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors duration-200 font-medium"
                        title="Download file"
                      >
                        <FaDownload className="w-4 h-4" />
                        Download
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setItemToDelete(file);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-400 hover:text-red-300 transition-colors duration-200"
                      title="Delete file"
                      aria-label="Delete file"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-75 z-50">
            <div className="bg-gray-700 p-6 rounded-lg shadow-lg max-w-sm w-full border border-gray-600">
              <h3 className="text-lg font-semibold text-white mb-4">
                Create New Folder
              </h3>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="w-full p-2 rounded-md bg-gray-600 border-gray-500 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm mb-4"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 px-4 py-2 rounded-md text-gray-200 hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createSubfolder}
                  className="bg-orange-400 text-gray-200 px-4 py-2 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteModal && itemToDelete && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-75 z-50">
            <div className="bg-gray-700 p-6 rounded-lg shadow-lg max-w-sm w-full border border-gray-600">
              <h3 className="text-lg font-semibold text-white mb-4">
                Confirm Delete
              </h3>
              <p className="text-gray-200 mb-4">
                Are you sure you want to delete "{itemToDelete.name}"? This
                action cannot be undone.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                  }}
                  className="bg-gray-600 px-4 py-2 rounded-md text-gray-200 hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteItem(itemToDelete.id)}
                  className="bg-red-400 text-gray-200 px-4 py-2 rounded-md hover:bg-red-500 transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharePointFilesTab;
